/**
 * Segment↔locale mapping for runtime languages (O8). Deliberately tiny and
 * dependency-free so proxy.ts (middleware bundle) can import it without
 * pulling the full baked Dict in lib/i18n.ts.
 *
 * The aliases are IRREGULAR — `SI` is region-derived from `sl_SI` while `EN`
 * is language-derived from `en_US`, and `SR` is language-derived from `sr_RS`
 * (derivation would give `sr_SR`) — which is why this is a table, not a rule.
 * Every OTHER locale derives its segment from the language part uppercased
 * (`de_DE` → `DE`), and reconstructs as `xx_XX`. That round-trips for the
 * language==region locales the backend uses (de_DE, fr_FR, it_IT, …); any
 * other language≠region locale (e.g. en_GB) needs its own alias row here.
 */
export const SEGMENT_ALIASES: Record<string, string> = {
  SI: "sl_SI",
  EN: "en_US",
  SR: "sr_RS",
};

/** Shape check only — "does this look like a locale segment?" The [lang]
 *  layout is the authoritative gate (against portal.availableLocales). */
export function isLangSegment(v: string | undefined | null): v is string {
  return typeof v === "string" && /^[A-Z]{2}$/.test(v);
}

/** URL segment → backend locale code ("DE" → "de_DE", "SI" → "sl_SI"). */
export function localeForSegment(segment: string): string {
  return SEGMENT_ALIASES[segment] ?? `${segment.toLowerCase()}_${segment.toUpperCase()}`;
}

/** Backend locale code → URL segment ("de_DE" → "DE", "sl_SI" → "SI"). */
export function segmentForLocale(locale: string): string {
  for (const [seg, loc] of Object.entries(SEGMENT_ALIASES)) {
    if (loc === locale) return seg;
  }
  return (locale.split("_")[0] ?? locale).toUpperCase();
}

/**
 * A backend locale in words, for the editor's chrome — "sl_SI" → "Slovenian",
 * "de_DE" → "German" — so an author reads the language, not the code. Named in
 * the editor's own language (English); the code stays as the tooltip. Falls
 * back to the code where the runtime cannot name it.
 */
export function languageName(locale: string, inLocale = "en"): string {
  const lang = locale.split(/[_-]/)[0];
  if (!lang) return locale;
  try {
    return new Intl.DisplayNames([inLocale], { type: "language" }).of(lang) ?? locale;
  } catch {
    return locale;
  }
}
